import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  COLON_SEGMENTS,
  DYSPLASIA_GRADES,
  HISTOLOGY_TYPES,
  PARIS_CLASSIFICATIONS,
  PROCEDURE_INDICATIONS,
  PROCEDURE_TYPES,
  REMOVAL_METHODS,
  decide,
  type CreateEndoscopyReportRequest,
  type EndoscopyReportResponse,
  type ProcedureConsentResponse,
} from '@emr/shared';

import {
  createEndoscopyReport,
  createPolyp,
  createProcedureConsent,
  fetchEndoscopyReports,
  fetchProcedureConsents,
  recordPolypPathology,
} from '../api/gastroenterology.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { Badge, Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';

/**
 * The gastroenterology module's real, patient-scoped surface (`BACKLOG.md` M5). Reachable directly
 * by URL even with the catalog entry showing an upgrade prompt for an unlicensed org, matching
 * `PatientDermatologyPage.tsx`'s/`PatientAestheticsPage.tsx`'s own stated reasoning for re-applying
 * `GatedRoute` here.
 *
 * Two sections, both behind `chart:write`:
 *
 *   - **Procedure consent** — the one consent concept this module needs (`treatment_general`).
 *   - **Structured endoscopy reports** — bowel prep, timing, indication, and a nested polyp list per
 *     report, each polyp showing the UNREVIEWED surveillance recommendation and billing-modifier
 *     guidance the server computes at read time. Every UNREVIEWED value is labeled as such in the UI,
 *     not just the API response — `claude-kit/rules/invariants.md`'s "never hard-code an unverified
 *     clinical threshold as if it were validated fact" applies to what a clinician sees, not only to
 *     the code that computed it.
 */
export function PatientGastroenterologyPage() {
  const { t } = useTranslation('gastroenterology');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <p className="m-0 text-sm text-danger">{t('common.noPatientId')}</p>;
  }

  return (
    <GatedRoute title={t('pageTitle')} capability="chart:read" feature="gastroenterologyModule">
      <div className="flex flex-col gap-6">
        <ConsentSection patientId={patientId} />
        <ReportsSection patientId={patientId} />
      </div>
    </GatedRoute>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return <h2 className="m-0 text-lg font-semibold text-ink">{children}</h2>;
}

// --- Procedure consent ----------------------------------------------------------------------------

function ConsentSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('gastroenterology');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['gastroenterology', 'consents', patientId];

  const query = useQuery<readonly ProcedureConsentResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchProcedureConsents(patientId, signal),
  });

  const [signedOn, setSignedOn] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createProcedureConsent(patientId, {
        signedOn,
        method: 'in_person',
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setSignedOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('consent.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('consent.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('consent.emptyMessage')}
        >
          {(consents) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {consents.map((consent) => (
                <li key={consent.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 text-ink">
                    {t('consent.signedOn', { date: consent.signedOn })}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <Field
              label={t('consent.signedOnFieldLabel')}
              type="date"
              value={signedOn}
              onChange={(event) => {
                setSignedOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('consent.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('consent.createError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Structured endoscopy reports ------------------------------------------------------------------

function ReportsSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('gastroenterology');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const reportsQueryKey = ['gastroenterology', 'reports', patientId];
  const consentsQueryKey = ['gastroenterology', 'consents', patientId];

  const reportsQuery = useQuery<readonly EndoscopyReportResponse[]>({
    queryKey: reportsQueryKey,
    queryFn: ({ signal }) => fetchEndoscopyReports(patientId, signal),
  });
  const consentsQuery = useQuery<readonly ProcedureConsentResponse[]>({
    queryKey: consentsQueryKey,
    queryFn: ({ signal }) => fetchProcedureConsents(patientId, signal),
  });

  const [procedureType, setProcedureType] =
    useState<(typeof PROCEDURE_TYPES)[number]>('colonoscopy');
  const [indication, setIndication] = useState<(typeof PROCEDURE_INDICATIONS)[number]>('screening');
  const [performedOn, setPerformedOn] = useState('');
  const [consentDocumentId, setConsentDocumentId] = useState('');

  const create = useMutation({
    mutationFn: () => {
      const input: CreateEndoscopyReportRequest = {
        procedureType,
        indication,
        performedOn,
        consentDocumentId,
        piecemealResectionOfLargePolyp: false,
        confidentialityReasons: [],
      };
      return createEndoscopyReport(patientId, input);
    },
    onSuccess: () => {
      setPerformedOn('');
      void queryClient.invalidateQueries({ queryKey: reportsQueryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('reports.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('reports.description')}</p>
      <Panel>
        <QuerySection
          query={reportsQuery}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('reports.emptyMessage')}
        >
          {(reports) => (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {reports.map((report) => (
                <ReportRow
                  key={report.id}
                  patientId={patientId}
                  report={report}
                  mayWrite={mayWrite}
                  onPolypChanged={() => {
                    void queryClient.invalidateQueries({ queryKey: reportsQueryKey });
                  }}
                />
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          consentsQuery.data === undefined || consentsQuery.data.length === 0 ? (
            <p className="m-0 mt-4 text-sm text-ink-muted">{t('reports.noConsentMessage')}</p>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                create.mutate();
              }}
              className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
            >
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t('reports.procedureTypeLabel')}
                </span>
                <select
                  value={procedureType}
                  onChange={(event) => {
                    setProcedureType(event.target.value as (typeof PROCEDURE_TYPES)[number]);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {PROCEDURE_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`vocab.procedureTypes.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t('reports.indicationLabel')}</span>
                <select
                  value={indication}
                  onChange={(event) => {
                    setIndication(event.target.value as (typeof PROCEDURE_INDICATIONS)[number]);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {PROCEDURE_INDICATIONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`vocab.procedureIndications.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label={t('reports.performedOnLabel')}
                type="date"
                value={performedOn}
                onChange={(event) => {
                  setPerformedOn(event.target.value);
                }}
                required
              />
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t('reports.consentLabel')}</span>
                <select
                  value={consentDocumentId}
                  onChange={(event) => {
                    setConsentDocumentId(event.target.value);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  <option value="">{t('common.select')}</option>
                  {consentsQuery.data.map((consent) => (
                    <option key={consent.id} value={consent.id}>
                      {consent.signedOn}
                    </option>
                  ))}
                </select>
              </label>
              <Button type="submit" pending={create.isPending} disabled={consentDocumentId === ''}>
                {t('reports.recordButton')}
              </Button>
            </form>
          )
        ) : null}
        {create.isError ? <ErrorText message={t('reports.createError')} /> : null}
      </Panel>
    </section>
  );
}

function ReportRow({
  patientId,
  report,
  mayWrite,
  onPolypChanged,
}: {
  readonly patientId: string;
  readonly report: EndoscopyReportResponse;
  readonly mayWrite: boolean;
  readonly onPolypChanged: () => void;
}) {
  const { t } = useTranslation('gastroenterology');

  const [location, setLocation] = useState<(typeof COLON_SEGMENTS)[number]>('sigmoid_colon');
  const [sizeMm, setSizeMm] = useState('5');
  const [parisClassification, setParisClassification] =
    useState<(typeof PARIS_CLASSIFICATIONS)[number]>('0-Is');
  const [removalMethod, setRemovalMethod] =
    useState<(typeof REMOVAL_METHODS)[number]>('cold_snare');

  const addPolyp = useMutation({
    mutationFn: () =>
      createPolyp(patientId, report.id, {
        location,
        sizeMm: Number(sizeMm),
        parisClassification,
        removalMethod,
      }),
    onSuccess: onPolypChanged,
  });

  return (
    <li className="rounded-control border border-line p-3 text-sm">
      <p className="m-0 font-medium text-ink">
        {t(`vocab.procedureTypes.${report.procedureType}`)} — {report.performedOn}
      </p>
      <p className="m-0 text-ink-muted">
        {t(`vocab.procedureIndications.${report.indication}`)}
        {report.bowelPrepTotal !== null
          ? ` — ${t('reports.bbpsSuffix', { total: report.bowelPrepTotal })}`
          : ''}
      </p>

      {/* UNREVIEWED clinical/billing guidance — always labeled, never presented as settled. */}
      <div className="mt-2 rounded-control border border-warning p-2 text-xs text-ink">
        <Badge tone="warning">{t('reports.unreviewedBadge')}</Badge>
        <p className="m-0 mt-1">
          {t('reports.surveillanceSummary', {
            reason: report.surveillanceRecommendation.reason,
            min: report.surveillanceRecommendation.minYears,
            max: report.surveillanceRecommendation.maxYears,
          })}
        </p>
        {report.billingModifiers.map((modifier) =>
          modifier.modifier === null ? null : (
            <p key={modifier.payerCategory} className="m-0 mt-1">
              {t('reports.billingModifierLine', {
                payer: t(`vocab.payerCategories.${modifier.payerCategory}`),
                modifier: modifier.modifier,
              })}
            </p>
          ),
        )}
      </div>

      <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
        {report.polyps.map((polyp) => (
          <PolypRow
            key={polyp.id}
            patientId={patientId}
            reportId={report.id}
            polyp={polyp}
            mayWrite={mayWrite}
            onChanged={onPolypChanged}
          />
        ))}
      </ul>

      {mayWrite ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            addPolyp.mutate();
          }}
          className="mt-2 flex flex-wrap items-end gap-2 border-t border-row-line pt-2"
        >
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink">{t('reports.polypLocationLabel')}</span>
            <select
              value={location}
              onChange={(event) => {
                setLocation(event.target.value as (typeof COLON_SEGMENTS)[number]);
              }}
              className="rounded-control border border-field-line bg-field px-2 py-1 text-xs text-ink"
            >
              {COLON_SEGMENTS.map((value) => (
                <option key={value} value={value}>
                  {t(`vocab.colonSegments.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <Field
            label={t('reports.polypSizeLabel')}
            type="number"
            min={1}
            value={sizeMm}
            onChange={(event) => {
              setSizeMm(event.target.value);
            }}
            className="max-w-[8ch]"
          />
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink">{t('reports.parisLabel')}</span>
            <select
              value={parisClassification}
              onChange={(event) => {
                setParisClassification(
                  event.target.value as (typeof PARIS_CLASSIFICATIONS)[number],
                );
              }}
              className="rounded-control border border-field-line bg-field px-2 py-1 text-xs text-ink"
            >
              {PARIS_CLASSIFICATIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink">{t('reports.removalMethodLabel')}</span>
            <select
              value={removalMethod}
              onChange={(event) => {
                setRemovalMethod(event.target.value as (typeof REMOVAL_METHODS)[number]);
              }}
              className="rounded-control border border-field-line bg-field px-2 py-1 text-xs text-ink"
            >
              {REMOVAL_METHODS.map((value) => (
                <option key={value} value={value}>
                  {t(`vocab.removalMethods.${value}`)}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="secondary" pending={addPolyp.isPending}>
            {t('reports.addPolypButton')}
          </Button>
        </form>
      ) : null}
      {addPolyp.isError ? <ErrorText message={t('reports.addPolypError')} /> : null}
    </li>
  );
}

function PolypRow({
  patientId,
  reportId,
  polyp,
  mayWrite,
  onChanged,
}: {
  readonly patientId: string;
  readonly reportId: string;
  readonly polyp: EndoscopyReportResponse['polyps'][number];
  readonly mayWrite: boolean;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation('gastroenterology');
  const [histology, setHistology] = useState<(typeof HISTOLOGY_TYPES)[number]>('tubular_adenoma');
  const [dysplasiaGrade, setDysplasiaGrade] = useState<(typeof DYSPLASIA_GRADES)[number] | ''>('');

  const record = useMutation({
    mutationFn: () =>
      recordPolypPathology(patientId, reportId, polyp.id, {
        histology,
        dysplasiaGrade: dysplasiaGrade === '' ? null : dysplasiaGrade,
      }),
    onSuccess: onChanged,
  });

  return (
    <li className="flex flex-col gap-1 rounded-control border border-line p-2 text-xs sm:flex-row sm:items-center sm:justify-between">
      <p className="m-0 break-words whitespace-normal text-ink [overflow-wrap:anywhere]">
        {t(`vocab.colonSegments.${polyp.location}`)} — {polyp.sizeMm}mm —{' '}
        {polyp.parisClassification} —{' '}
        {polyp.pathologyStatus === 'resulted' && polyp.histology !== null
          ? t(`vocab.histologyTypes.${polyp.histology}`)
          : t('reports.pathologyPending')}
      </p>
      {mayWrite && polyp.pathologyStatus === 'pending' ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            record.mutate();
          }}
          className="flex flex-wrap items-end gap-2"
        >
          <select
            value={histology}
            onChange={(event) => {
              setHistology(event.target.value as (typeof HISTOLOGY_TYPES)[number]);
            }}
            className="rounded-control border border-field-line bg-field px-2 py-1 text-xs text-ink"
          >
            {HISTOLOGY_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`vocab.histologyTypes.${value}`)}
              </option>
            ))}
          </select>
          <select
            value={dysplasiaGrade}
            onChange={(event) => {
              setDysplasiaGrade(event.target.value as (typeof DYSPLASIA_GRADES)[number] | '');
            }}
            className="rounded-control border border-field-line bg-field px-2 py-1 text-xs text-ink"
          >
            <option value="">{t('reports.noDysplasia')}</option>
            {DYSPLASIA_GRADES.map((value) => (
              <option key={value} value={value}>
                {t(`vocab.dysplasiaGrades.${value}`)}
              </option>
            ))}
          </select>
          <Button type="submit" variant="secondary" pending={record.isPending}>
            {t('reports.recordPathologyButton')}
          </Button>
        </form>
      ) : null}
    </li>
  );
}
